import { useContext, useState } from "react";
import primaryComponents from "../../../primaryComponents";
import secondaryComponents from "../..";
import { formatCurrency } from "../../../../helpers";
import { useOtpAuthTrigger } from "../../../../hooks/useOtpAuthTrigger";
import { ToastContext } from "../../../../hooks/context";
import { removeItemDiscount } from "../../../../services/sales-point";
import { useMutation } from "react-query";

const LineDiscountSelector = ({
  item,
  storeInfo,
  params,
  onSetLineDiscount,
  onRemoveLineDiscount,
}) => {
  const [isActive, setIsActive] = useState(false);
  const triggerToast = useContext(ToastContext);

  const existingDiscount = item?.manual_discount;
  const currencyCode = storeInfo?.currency_code;

  return (
    <span>
      <span
        className="d-flex cursor-pointer align-items-center"
        style={{
          background: existingDiscount ? "#E8F7EE" : "hsla(0, 0%, 94%, 1)",
          color: existingDiscount ? "#19AE57" : "#333",
          padding: "4px 8px",
          borderRadius: "8px",
          width: "fit-content",
          fontSize: "12px",
          fontWeight: existingDiscount ? 600 : 400,
          border: existingDiscount ? "1px solid #75CE9A" : "1px solid #e0e0e0",
          marginTop: "4px",
        }}
        onClick={() => setIsActive((prev) => !prev)}
      >
        {existingDiscount
          ? existingDiscount.type === "percentage"
            ? `${Number(existingDiscount.amount).toFixed(2)}% OFF`
            : `-${formatCurrency({
                value: existingDiscount.amount,
                currencyCode,
              })}/unit`
          : "+ Discount"}
      </span>

      <secondaryComponents.Modal
        isActive={isActive}
        width={460}
        closeModal={() => setIsActive(false)}
      >
        <LineDiscountModalContent
          item={item}
          storeInfo={storeInfo}
          params={params}
          isActive={isActive}
          setIsActive={setIsActive}
          triggerToast={triggerToast}
          onSetLineDiscount={onSetLineDiscount}
          onRemoveLineDiscount={onRemoveLineDiscount}
        />
      </secondaryComponents.Modal>
    </span>
  );
};

export default LineDiscountSelector;

const LineDiscountModalContent = ({
  item,
  storeInfo,
  params,
  setIsActive,
  triggerToast,
  onSetLineDiscount,
  onRemoveLineDiscount,
}) => {
  const existingDiscount = item?.manual_discount;
  const currencyCode = storeInfo?.currency_code;

  const [discountType, setDiscountType] = useState(
    existingDiscount?.type || "flat",
  );
  const [discountValue, setDiscountValue] = useState(
    existingDiscount?.amount || "",
  );

  const { handleOrderModuleAuth, isLoadingAuthAction } = useOtpAuthTrigger({
    storeId: storeInfo?.id,
    params,
  });

  const { mutate: callRemoveDiscountApi, isLoading: isRemovingDiscount } =
    useMutation(({ cart_item_id, store_id }) =>
      removeItemDiscount({ cart_item_id, store_id }),
    );

  const basePrice = Number(
    item?.is_custom_price && item?.custom_price
      ? item?.custom_price
      : item?.total_price_without_discount && item?.quantity
        ? Number(item.total_price_without_discount) / Number(item.quantity)
        : item?.unit_price || 0,
  );

  const numVal = Number(discountValue) || 0;
  const discountPerUnit =
    discountType === "percentage" ? (numVal / 100) * basePrice : numVal;
  const newUnitPrice = Math.max(0, basePrice - discountPerUnit);

  const handleApply = () => {
    if (basePrice <= 0) {
      return triggerToast(
        "Discount can not be applied to an item priced at zero",
        "warning",
      );
    }

    if (!discountValue || isNaN(numVal) || numVal <= 0) {
      return triggerToast("Please enter a valid discount amount", "warning");
    }

    if (discountType === "percentage" && numVal > 100) {
      return triggerToast("Percentage discount cannot exceed 100%", "warning");
    }

    if (discountType === "flat" && numVal > basePrice) {
      return triggerToast(
        "Discount amount can not be more than the item price",
        "warning",
      );
    }

    handleOrderModuleAuth({
      actionKey: "add_discount_to_order",
      onSuccessCallBack: () => {
        onSetLineDiscount(
          {
            type: discountType,
            amount: numVal,
          },
          newUnitPrice,
        );
        triggerToast("Line discount applied", "success");
        setIsActive(false);
      },
    });
  };

  const handleRemove = () => {
    if (item?.cart_item_id && storeInfo?.id) {
      callRemoveDiscountApi(
        { cart_item_id: item.cart_item_id, store_id: storeInfo.id },
        {
          onSuccess: () => {
            onRemoveLineDiscount(basePrice);
            triggerToast("Discount removed from line item", "success");
            setIsActive(false);
          },
        },
      );
    } else {
      onRemoveLineDiscount(basePrice);
      triggerToast("Discount removed", "success");
      setIsActive(false);
    }
  };

  return (
    <div
      className="p-3"
      style={{ display: "flex", flexDirection: "column", gap: "12px" }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          {existingDiscount ? "Update Line Discount" : "Apply Line Discount"}
        </h5>
        {existingDiscount && (
          <primaryComponents.Button
            classNames="btn btn--outline-red smallBtn"
            style={{ height: "30px", fontSize: "12px", padding: "0 10px" }}
            onClick={handleRemove}
            isLoading={isRemovingDiscount}
            disabled={isRemovingDiscount}
          >
            Remove Discount
          </primaryComponents.Button>
        )}
      </div>

      <p className="gray--text small-text-size mb-0">
        Product: <strong>{item?.cart_item_name}</strong>
      </p>

      <div className="d-flex my-1" style={{ gap: "10px" }}>
        <primaryComponents.BoxedRadioInput
          name={`billDiscountType_${item?.id}`}
          id={`billDiscountTypeFlat_${item?.id}`}
          isChecked={discountType === "flat"}
          setRadioValue={() => setDiscountType("flat")}
        >
          Flat Amount ({currencyCode})
        </primaryComponents.BoxedRadioInput>
        <primaryComponents.BoxedRadioInput
          name={`billDiscountType_${item?.id}`}
          id={`billDiscountTypePercent_${item?.id}`}
          isChecked={discountType === "percentage"}
          setRadioValue={() => setDiscountType("percentage")}
        >
          Percentage (%)
        </primaryComponents.BoxedRadioInput>
      </div>

      <primaryComponents.InputField
        label={
          discountType === "flat"
            ? `Discount Amount per unit (${currencyCode})`
            : "Discount Percentage (%)"
        }
        placeholder={discountType === "flat" ? `${currencyCode} 0.00` : "0%"}
        value={discountValue}
        type="number"
        min="0"
        max={discountType === "percentage" ? "100" : undefined}
        step={0.01}
        onChange={({ target: { value } }) => setDiscountValue(value)}
      />

      <div
        className="p-2 rounded"
        style={{
          background: "#f8f9fa",
          border: "1px solid #eee",
          fontSize: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "4px",
        }}
      >
        <div className="d-flex justify-content-between">
          <span className="gray--text">Base Unit Price:</span>
          <span>{formatCurrency({ value: basePrice, currencyCode })}</span>
        </div>
        <div className="d-flex justify-content-between">
          <span className="gray--text">Discount per unit:</span>
          <span className="text-danger font-weight-semibold">
            -{formatCurrency({ value: discountPerUnit, currencyCode })}
            {discountType === "percentage" && discountValue
              ? ` (${Number(discountValue).toFixed(2)}%)`
              : ""}
          </span>
        </div>
        <div className="d-flex justify-content-between font-weight-bold">
          <span>New Unit Price:</span>
          <span className="text-success">
            {formatCurrency({ value: newUnitPrice, currencyCode })}
          </span>
        </div>
        <div className="d-flex justify-content-between">
          <span className="gray--text">
            Total Line Savings ({item?.quantity}x):
          </span>
          <span className="text-danger font-weight-semibold">
            -
            {formatCurrency({
              value: discountPerUnit * Number(item?.quantity || 1),
              currencyCode,
            })}
          </span>
        </div>
      </div>

      <div className="d-flex mt-2" style={{ gap: "10px" }}>
        <primaryComponents.Button
          classNames="btn btn--outline cover"
          onClick={() => setIsActive(false)}
        >
          Cancel
        </primaryComponents.Button>
        <primaryComponents.Button
          classNames="btn btn--primary cover"
          onClick={handleApply}
          isLoading={isLoadingAuthAction}
          disabled={isLoadingAuthAction}
          loadingText="Applying..."
        >
          {existingDiscount ? "Update Discount" : "Apply Discount"}
        </primaryComponents.Button>
      </div>
    </div>
  );
};
